import { Box, Button, EmptyState, Skeleton, Text } from "@salestrak/ui";

import { BaseItem } from "#app/components/issue-view/activity/base-item";
import { useIssueView } from "#app/components/issue-view";
import { EntityTag } from "#app/components/entity/entity-tag";
import type { ActivityFeedRow } from "#app/widgets/definitions/activity-list/use-activity-list";
import { useMemberActivity } from "./use-member-activity";

/** The Overview section shows the latest few; the Activity tab pages on. */
const SECTION_LIMIT = 5;
const TAB_PAGE_SIZE = 25;

function ActivityEmpty() {
  return (
    <EmptyState
      description="Updates, comments and changes this member makes on Projects you can see appear here."
      icon="activity"
      media="circle"
      size="sm"
      title="No activity yet"
    />
  );
}

/** The member's actions as the rows a project's timeline draws, each naming
 *  its project and opening it on press. */
function MemberActivityRows({ rows }: { rows: ActivityFeedRow[] }) {
  const { openIssueById } = useIssueView();
  return (
    <Box direction="column">
      {rows.map((activity, index) => (
        <Box
          key={`${activity.activityId}-${index}`}
          as="button"
          type="button"
          direction="row"
          align="center"
          group="row"
          paddingX="xs"
          marginBottom="md"
          fill="width"
          minWidth={0}
          onClick={() => openIssueById(activity.issueId, { source: "profile" })}
        >
          <Box flex={1} minWidth={0}>
            <BaseItem
              activity={activity}
              issueId={activity.issueId}
              last={index === rows.length - 1}
              bottomSpacing={false}
              truncate
              timeReading="relative"
              trailing={
                activity.issue ? (
                  <Text size="sm">
                    {" on "}
                    <Text size="sm" hoverInk="row">
                      <EntityTag
                        entity={{
                          kind: "issue",
                          id: activity.issue.issueId,
                          issue: activity.issue,
                        }}
                        display="label"
                        as="text"
                        size="sm"
                        weight="semibold"
                        preview
                        interactive={false}
                      />
                    </Text>
                  </Text>
                ) : null
              }
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function ActivitySkeleton() {
  return (
    <Box direction="column" gap="sm">
      <Skeleton height={16} />
      <Skeleton height={16} />
      <Skeleton height={16} />
    </Box>
  );
}

/** The Overview tab's Activity section: the member's latest actions. */
export function MemberProfileActivitySection({ userId }: { userId: string | null }) {
  const { rows, initialLoading } = useMemberActivity(userId, SECTION_LIMIT);
  return (
    <Box direction="column" gap="sm">
      <Text size="sm" weight="semibold">
        Activity
      </Text>
      {initialLoading ? (
        <ActivitySkeleton />
      ) : rows.length === 0 ? (
        <ActivityEmpty />
      ) : (
        <MemberActivityRows rows={rows} />
      )}
    </Box>
  );
}

/** The Activity tab: the member's whole feed, a page at a time. */
export function MemberProfileActivityTab({ userId }: { userId: string | null }) {
  const { rows, initialLoading, loadingMore, hasMore, loadMore } = useMemberActivity(
    userId,
    TAB_PAGE_SIZE,
  );
  return (
    <Box direction="column" gap="sm" paddingX="md" paddingY="md">
      {initialLoading ? (
        <ActivitySkeleton />
      ) : rows.length === 0 ? (
        <ActivityEmpty />
      ) : (
        <>
          <MemberActivityRows rows={rows} />
          {hasMore ? (
            <Button loading={loadingMore} onClick={loadMore} size="sm" variant="outline">
              Show more
            </Button>
          ) : null}
        </>
      )}
    </Box>
  );
}
